// src/pages/GridIntegrantesCRUD.jsx - Gestión de Integrantes con Sistema de Niveles
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  Paper,
  Container,
  Tooltip,
  IconButton,
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  LinearProgress
} from '@mui/material';

import {
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  TrendingUp as NivelIcon,
  EmojiEvents as TrophyIcon,
  StarBorder as StarIcon
} from '@mui/icons-material';

import { obtenerDepartamentos, obtenerMunicipios } from '../actions/EntidadAction';
import useStyles from '../theme/useStyles';

// Estilos Alianza para TextField
const textFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(30, 136, 229, 0.3)',
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: '#1E88E5',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1E88E5',
      borderWidth: '2px',
    },
    '&.Mui-error fieldset': {
      borderColor: '#E63946',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#757575',
    '&.Mui-focused': {
      color: '#1E88E5',
    },
    '&.Mui-error': {
      color: '#E63946',
    },
  },
};

// DATOS MOCKUP - Niveles del sistema
const NIVELES_MOCKUP = [
  { nivelID: 1, nombre: 'Simpatizante', icono: '🌱', color: '#4CAF50', descripcion: 'Nivel inicial' },
  { nivelID: 2, nombre: 'Colaborador', icono: '🤝', color: '#2196F3', descripcion: 'Participa activamente' },
  { nivelID: 3, nombre: 'Activista', icono: '⚡', color: '#FF9800', descripcion: 'Líder comunitario' },
  { nivelID: 4, nombre: 'Militante', icono: '🔥', color: '#F44336', descripcion: 'Comprometido total' },
  { nivelID: 5, nombre: 'Dirigente', icono: '👑', color: '#9C27B0', descripcion: 'Liderazgo regional' }
];

// DATOS MOCKUP - Integrantes de ejemplo (hasta que venga del API real)
const INTEGRANTES_MOCKUP = [
  {
    integranteID: 1,
    nombre: 'Carlos Andrés',
    apellido: 'Gómez Martínez',
    nombreCompleto: 'Carlos Andrés Gómez Martínez',
    tipoDocumento: 'CC',
    documento: '1098765432',
    email: 'carlos.gomez@email.com',
    telefono: '3201234567',
    nivelID: 3,
    nivelNombre: 'Activista',
    municipio: 'Bucaramanga',
    departamento: 'Santander',
    estado: 'Activo',
    fechaIngreso: '2024-01-15',
    progreso: 75
  },
  {
    integranteID: 2,
    nombre: 'María Fernanda',
    apellido: 'Rodríguez Silva',
    nombreCompleto: 'María Fernanda Rodríguez Silva',
    tipoDocumento: 'CC',
    documento: '1097654321',
    email: 'maria.rodriguez@email.com',
    telefono: '3109876543',
    nivelID: 2,
    nivelNombre: 'Colaborador',
    municipio: 'Floridablanca',
    departamento: 'Santander',
    estado: 'Activo',
    fechaIngreso: '2024-02-20',
    progreso: 45
  },
  {
    integranteID: 3,
    nombre: 'Juan Pablo',
    apellido: 'Hernández López',
    nombreCompleto: 'Juan Pablo Hernández López',
    tipoDocumento: 'CC',
    documento: '1096543210',
    email: 'juan.hernandez@email.com',
    telefono: '3187654321',
    nivelID: 4,
    nivelNombre: 'Militante',
    municipio: 'Girón',
    departamento: 'Santander',
    estado: 'Activo',
    fechaIngreso: '2023-11-10',
    progreso: 90
  },
  {
    integranteID: 4,
    nombre: 'Ana Lucía',
    apellido: 'Torres Ramírez',
    nombreCompleto: 'Ana Lucía Torres Ramírez',
    tipoDocumento: 'CC',
    documento: '1095432109',
    email: 'ana.torres@email.com',
    telefono: '3176543210',
    nivelID: 1,
    nivelNombre: 'Simpatizante',
    municipio: 'Piedecuesta',
    departamento: 'Santander',
    estado: 'Pendiente',
    fechaIngreso: '2024-08-05',
    progreso: 20
  },
  {
    integranteID: 5,
    nombre: 'Luis Fernando',
    apellido: 'Moreno Castro',
    nombreCompleto: 'Luis Fernando Moreno Castro',
    tipoDocumento: 'CC',
    documento: '1094321098',
    email: 'luis.moreno@email.com',
    telefono: '3165432109',
    nivelID: 5,
    nivelNombre: 'Dirigente',
    municipio: 'Bucaramanga',
    departamento: 'Santander',
    estado: 'Activo',
    fechaIngreso: '2023-06-01',
    progreso: 100
  },
  {
    integranteID: 6,
    nombre: 'Diana Carolina',
    apellido: 'Ruiz Vargas',
    nombreCompleto: 'Diana Carolina Ruiz Vargas',
    tipoDocumento: 'CC',
    documento: '1093210987',
    email: 'diana.ruiz@email.com',
    telefono: '3154321098',
    nivelID: 2,
    nivelNombre: 'Colaborador',
    municipio: 'Lebrija',
    departamento: 'Santander',
    estado: 'Activo',
    fechaIngreso: '2024-03-12',
    progreso: 55
  },
  {
    integranteID: 7,
    nombre: 'Santiago',
    apellido: 'Díaz Peña',
    nombreCompleto: 'Santiago Díaz Peña',
    tipoDocumento: 'CC',
    documento: '1092109876',
    email: 'santiago.diaz@email.com',
    telefono: '3143210987',
    nivelID: 3,
    nivelNombre: 'Activista',
    municipio: 'Barrancabermeja',
    departamento: 'Santander',
    estado: 'Inactivo',
    fechaIngreso: '2024-01-28',
    progreso: 60
  },
  {
    integranteID: 8,
    nombre: 'Camila Andrea',
    apellido: 'Reyes Ortiz',
    nombreCompleto: 'Camila Andrea Reyes Ortiz',
    tipoDocumento: 'CC',
    documento: '1091098765',
    email: 'camila.reyes@email.com',
    telefono: '3132109876',
    nivelID: 1,
    nivelNombre: 'Simpatizante',
    municipio: 'San Gil',
    departamento: 'Santander',
    estado: 'Pendiente',
    fechaIngreso: '2024-09-14',
    progreso: 15
  }
];

const GridIntegrantesCRUD = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.session);
  
  // Estados principales
  const [integrantes, setIntegrantes] = useState(INTEGRANTES_MOCKUP);
  const [niveles, setNiveles] = useState(NIVELES_MOCKUP);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [municipiosFiltradosPorDepartamento, setMunicipiosFiltradosPorDepartamento] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Estados de filtros y paginación
  const [filtro, setFiltro] = useState('');
  const [filtroNivel, setFiltroNivel] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroDepartamento, setFiltroDepartamento] = useState('');
  const [filtroMunicipio, setFiltroMunicipio] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estados del modal de detalles
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedIntegrante, setSelectedIntegrante] = useState(null);
  
  // Estados de mensajes
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Cargar datos (en producción vendría del API)
  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar departamentos desde API
      const departamentosResponse = await obtenerDepartamentos();
      if (departamentosResponse.status === 200) {
        setDepartamentos(departamentosResponse.data);
      }
      
      // Cargar municipios desde API
      const municipiosResponse = await obtenerMunicipios();
      if (municipiosResponse.status === 200) {
        setMunicipios(municipiosResponse.data);
      }

      // Simulación de carga de integrantes
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // En producción:
      // const response = await obtenerIntegrantes();
      // setIntegrantes(response.data);
      
      setSuccessMessage('Datos cargados correctamente');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setErrorMessage('Error al cargar los integrantes');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar municipios cuando se selecciona un departamento
  useEffect(() => {
    if (filtroDepartamento) {
      const municipiosDelDepartamento = municipios.filter(
        m => m.departamentoID === parseInt(filtroDepartamento)
      );
      setMunicipiosFiltradosPorDepartamento(municipiosDelDepartamento);
    } else {
      setMunicipiosFiltradosPorDepartamento([]);
    }
    
    // Limpiar selección de municipio si cambia el departamento
    if (filtroMunicipio && filtroDepartamento) {
      const municipioSeleccionado = municipios.find(m => m.municipioID === parseInt(filtroMunicipio));
      if (municipioSeleccionado && municipioSeleccionado.departamentoID !== parseInt(filtroDepartamento)) {
        setFiltroMunicipio('');
      }
    }
  }, [filtroDepartamento, municipios, filtroMunicipio]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await cargarDatos();
    setRefreshing(false);
  };

  // Obtener info de nivel
  const getNivelInfo = (nivelID) => {
    return niveles.find(n => n.nivelID === nivelID) || niveles[0];
  };

  // Filtrar integrantes
  const integrantesFiltrados = integrantes.filter((integrante) => {
    const matchFiltro = !filtro || 
      integrante.nombreCompleto.toLowerCase().includes(filtro.toLowerCase()) ||
      integrante.documento.includes(filtro) ||
      integrante.email.toLowerCase().includes(filtro.toLowerCase());
    
    const matchNivel = !filtroNivel || integrante.nivelID === parseInt(filtroNivel);
    const matchEstado = !filtroEstado || integrante.estado === filtroEstado;
    
    // Filtro por departamento
    const matchDepartamento = !filtroDepartamento || integrante.departamento === 
      departamentos.find(d => d.departamentoID === parseInt(filtroDepartamento))?.nombre;
    
    // Filtro por municipio
    const matchMunicipio = !filtroMunicipio || integrante.municipio === 
      municipios.find(m => m.municipioID === parseInt(filtroMunicipio))?.nombre;
    
    return matchFiltro && matchNivel && matchEstado && matchDepartamento && matchMunicipio;
  });

  // Paginación
  const integrantesPaginados = integrantesFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Abrir detalles
  const handleOpenDetails = (integrante) => {
    setSelectedIntegrante(integrante);
    setOpenDetailsModal(true);
  };

  // Chip de estado
  const getEstadoChip = (estado) => {
    const colores = {
      'Activo': { bg: '#E8F5E9', color: '#2E7D32', label: 'Activo' },
      'Pendiente': { bg: '#FFF3E0', color: '#E65100', label: 'Pendiente' },
      'Inactivo': { bg: '#FFEBEE', color: '#C62828', label: 'Inactivo' }
    };
    
    const config = colores[estado] || colores['Activo'];
    
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bg,
          color: config.color,
          fontWeight: 600,
          fontSize: '0.75rem'
        }}
      />
    );
  };

  // Chip de nivel
  const getNivelChip = (nivelID) => {
    const nivel = getNivelInfo(nivelID);
    
    return (
      <Chip
        icon={<span style={{ fontSize: '1.2rem' }}>{nivel.icono}</span>}
        label={nivel.nombre}
        size="small"
        sx={{
          backgroundColor: `${nivel.color}15`,
          color: nivel.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          border: `1.5px solid ${nivel.color}`,
          '& .MuiChip-icon': {
            marginLeft: '4px'
          }
        }}
      />
    );
  };

  // Estadísticas
  const stats = {
    total: integrantes.length,
    activos: integrantes.filter(i => i.estado === 'Activo').length,
    pendientes: integrantes.filter(i => i.estado === 'Pendiente').length,
    porNivel: niveles.map(nivel => ({
      ...nivel,
      cantidad: integrantes.filter(i => i.nivelID === nivel.nivelID).length
    }))
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: 7, 
        mb: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.35)',
        backdropFilter: 'blur(10px)',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.38)'
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="#1E88E5" gutterBottom>
              <PeopleIcon sx={{ fontSize: 40, mr: 1, verticalAlign: 'middle' }} />
              Gestión de Integrantes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de niveles y seguimiento de miembros de Alianza Social
            </Typography>
          </Box>
          
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                borderRadius: '12px',
                borderWidth: '2px',
                borderColor: '#1E88E5',
                color: '#1E88E5',
                '&:hover': {
                  borderWidth: '2px',
                  borderColor: '#1565C0',
                  backgroundColor: 'rgba(30, 136, 229, 0.04)'
                }
              }}
            >
              Actualizar
            </Button>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/register')}
              sx={{
                borderRadius: '12px',
                backgroundColor: '#1E88E5',
                '&:hover': {
                  backgroundColor: '#1565C0'
                }
              }}
            >
              Nuevo Integrante
            </Button>
          </Box>
        </Box>

        {/* Mensajes */}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
      </Box>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #1E88E5' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#1E88E5">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Integrantes
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 50, color: '#1E88E5', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #4CAF50' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#4CAF50">
                    {stats.activos}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Activos
                  </Typography>
                </Box>
                <StarIcon sx={{ fontSize: 50, color: '#4CAF50', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #FF9800' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="#FF9800">
                    {stats.pendientes}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pendientes
                  </Typography>
                </Box>
                <NivelIcon sx={{ fontSize: 50, color: '#FF9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: '16px', border: '2px solid #9C27B0' }}>
            <CardContent>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Distribución por Niveles
                </Typography>
                {stats.porNivel.slice(0, 3).map(nivel => (
                  <Box key={nivel.nivelID} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption">
                      {nivel.icono} {nivel.nombre}
                    </Typography>
                    <Typography variant="caption" fontWeight="bold" color={nivel.color}>
                      {nivel.cantidad}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Card sx={{ mb: 3, borderRadius: '16px', border: '2px solid rgba(30, 136, 229, 0.2)' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Búsqueda general */}
            <Grid item xs={12} sm={12} md={3} sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, documento o email..."
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: '#757575' }} />
                }}
                size="small"
                sx={textFieldStyles}
              />
            </Grid>

            {/* Filtro Departamento */}
            <Grid item xs={12} sm={6} md={3} sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                select
                label="Departamento"
                value={filtroDepartamento}
                onChange={(e) => {
                  setFiltroDepartamento(e.target.value);
                  setFiltroMunicipio('');
                }}
                size="small"
                sx={textFieldStyles}
              >
                <MenuItem value="">Todos</MenuItem>
                {departamentos.map((depto) => (
                  <MenuItem key={depto.departamentoID} value={depto.departamentoID}>
                    {depto.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Filtro Municipio */}
            <Grid item xs={12} sm={6} md={3} sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                select
                label="Municipio"
                value={filtroMunicipio}
                onChange={(e) => setFiltroMunicipio(e.target.value)}
                disabled={!filtroDepartamento}
                size="small"
                sx={textFieldStyles}
              >
                <MenuItem value="">Todos</MenuItem>
                {municipiosFiltradosPorDepartamento.map((muni) => (
                  <MenuItem key={muni.municipioID} value={muni.municipioID}>
                    {muni.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Filtro Nivel */}
            <Grid item xs={6} sm={4} md="auto" sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                select
                label="Nivel"
                value={filtroNivel}
                onChange={(e) => setFiltroNivel(e.target.value)}
                size="small"
                sx={textFieldStyles}
              >
                <MenuItem value="">Todos</MenuItem>
                {niveles.map((nivel) => (
                  <MenuItem key={nivel.nivelID} value={nivel.nivelID}>
                    {nivel.icono} {nivel.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Filtro Estado */}
            <Grid item xs={6} sm={4} md="auto" sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                select
                label="Estado"
                value={filtroEstado}
                onChange={(e) => setFiltroEstado(e.target.value)}
                size="small"
                sx={textFieldStyles}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="Activo">Activo</MenuItem>
                <MenuItem value="Pendiente">Pendiente</MenuItem>
                <MenuItem value="Inactivo">Inactivo</MenuItem>
              </TextField>
            </Grid>

            {/* Botón Limpiar - Solo icono, sin flexGrow para que tome solo el ancho necesario */}
            <Grid item xs={12} sm={4} md="auto">
              <Tooltip title="Limpiar filtros">
                <IconButton
                  onClick={() => {
                    setFiltro('');
                    setFiltroNivel('');
                    setFiltroEstado('');
                    setFiltroDepartamento('');
                    setFiltroMunicipio('');
                  }}
                  sx={{
                    width: '48px',
                    height: '40px',
                    borderRadius: '12px',
                    border: '2px solid #757575',
                    color: '#757575',
                    '&:hover': {
                      borderColor: '#1E88E5',
                      backgroundColor: 'rgba(30, 136, 229, 0.04)',
                      color: '#1E88E5'
                    }
                  }}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
              <CircularProgress size={60} sx={{ color: '#1E88E5' }} />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Persona
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Nivel
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Documento
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Contacto
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Ubicación
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Progreso
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Estado
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ backgroundColor: '#F5F5F5' }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Acciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {integrantesPaginados.map((integrante) => {
                      const nivelInfo = getNivelInfo(integrante.nivelID);
                      return (
                        <TableRow key={integrante.integranteID} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar 
                                sx={{ 
                                  bgcolor: nivelInfo.color, 
                                  width: 40, 
                                  height: 40,
                                  fontSize: '1.2rem'
                                }}
                              >
                                {integrante.nombre.charAt(0)}{integrante.apellido.charAt(0)}
                              </Avatar>
                              <Box ml={2}>
                                <Typography variant="body2" fontWeight="medium">
                                  {integrante.nombreCompleto}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Ingreso: {new Date(integrante.fechaIngreso).toLocaleDateString('es-CO')}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            {getNivelChip(integrante.nivelID)}
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2">
                              {integrante.tipoDocumento} {integrante.documento}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Box>
                              <Box display="flex" alignItems="center" mb={0.5}>
                                <EmailIcon fontSize="small" sx={{ mr: 0.5, color: '#757575' }} />
                                <Typography variant="caption">
                                  {integrante.email}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center">
                                <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: '#757575' }} />
                                <Typography variant="caption">
                                  {integrante.telefono}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <LocationIcon fontSize="small" sx={{ mr: 0.5, color: '#757575' }} />
                              <Box>
                                <Typography variant="caption" display="block">
                                  {integrante.municipio}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {integrante.departamento}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box sx={{ width: 80 }}>
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography variant="caption" fontWeight="bold" color={nivelInfo.color}>
                                  {integrante.progreso}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={integrante.progreso}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  backgroundColor: `${nivelInfo.color}20`,
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: nivelInfo.color,
                                    borderRadius: 3
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            {getEstadoChip(integrante.estado)}
                          </TableCell>
                          
                          <TableCell align="center">
                            <Tooltip title="Ver detalles">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDetails(integrante)}
                                sx={{ 
                                  color: '#1E88E5',
                                  '&:hover': { backgroundColor: 'rgba(30, 136, 229, 0.1)' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                sx={{ 
                                  color: '#FF9800',
                                  '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.1)' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginación */}
              <TablePagination
                component="div"
                count={integrantesFiltrados.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalles */}
      <Dialog 
        open={openDetailsModal} 
        onClose={() => setOpenDetailsModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PersonIcon sx={{ mr: 1, color: '#1E88E5' }} />
            <Typography variant="h6" fontWeight="bold">
              Detalles del Integrante
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedIntegrante && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: getNivelInfo(selectedIntegrante.nivelID).color,
                      width: 80,
                      height: 80,
                      fontSize: '2rem',
                      mr: 3
                    }}
                  >
                    {selectedIntegrante.nombre.charAt(0)}{selectedIntegrante.apellido.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {selectedIntegrante.nombreCompleto}
                    </Typography>
                    <Box mt={1}>
                      {getNivelChip(selectedIntegrante.nivelID)}
                      {' '}
                      {getEstadoChip(selectedIntegrante.estado)}
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Tipo de Documento
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIntegrante.tipoDocumento}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Número de Documento
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIntegrante.documento}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIntegrante.email}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Teléfono
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIntegrante.telefono}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Municipio
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIntegrante.municipio}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Departamento
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedIntegrante.departamento}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Fecha de Ingreso
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {new Date(selectedIntegrante.fechaIngreso).toLocaleDateString('es-CO', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Progreso en Nivel Actual
                </Typography>
                <Box display="flex" alignItems="center" mt={1}>
                  <LinearProgress 
                    variant="determinate" 
                    value={selectedIntegrante.progreso}
                    sx={{
                      flex: 1,
                      mr: 2,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: `${getNivelInfo(selectedIntegrante.nivelID).color}20`,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getNivelInfo(selectedIntegrante.nivelID).color,
                        borderRadius: 4
                      }
                    }}
                  />
                  <Typography variant="body1" fontWeight="bold" color={getNivelInfo(selectedIntegrante.nivelID).color}>
                    {selectedIntegrante.progreso}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDetailsModal(false)}
            sx={{
              borderRadius: '12px',
              color: '#757575'
            }}
          >
            Cerrar
          </Button>
          <Button 
            variant="contained"
            startIcon={<EditIcon />}
            sx={{
              borderRadius: '12px',
              backgroundColor: '#1E88E5',
              '&:hover': {
                backgroundColor: '#1565C0'
              }
            }}
          >
            Editar Integrante
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GridIntegrantesCRUD;